import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getSettings, PVQSettings } from '../utils/settings';
import { useProjectConfig } from '../hooks/useProjectConfig';

interface SidebarNavigationProps {
  activeSection: string;
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({ activeSection }) => {
  const [settings, setSettings] = useState<PVQSettings>(getSettings());
  const projectConfig = useProjectConfig();

  // Load settings on component mount and listen for changes
  useEffect(() => {
    const loadSettings = () => {
      setSettings(getSettings());
    };

    // Load initial settings
    loadSettings();

    // Listen for storage changes (when settings are updated in another tab or component)
    window.addEventListener('storage', loadSettings);

    // Custom event for same-tab settings changes
    window.addEventListener('pvq-settings-changed', loadSettings);

    return () => {
      window.removeEventListener('storage', loadSettings);
      window.removeEventListener('pvq-settings-changed', loadSettings);
    };
  }, []);

  // Get form sections from project config
  const projectSections = projectConfig.sidebarSections?.sections || [];

  // Filter and sort form sections based on project config and settings
  const formSections = projectSections
    .filter(section => {
      // Filter based on settings (e.g., performComprehensiveReview for review section)
      if (section.id === 'review') {
        return settings.performComprehensiveReview;
      }
      return true;
    })
    .sort((a, b) => a.order - b.order);

  // Build utility items dynamically based on project
  const utilityItems = [
    {
      id: 'roadmap',
      label: 'PI Alignment',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      )
    },
    ...(projectConfig.dataViewer?.enabled ? [{
      id: 'artifact-viewer',
      label: projectConfig.dataViewer.menuLabel,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      )
    }] : []),
    // NOC Dashboard - only for projects with NOC monitoring enabled
    ...(projectConfig.features.nocMonitoring ? [{
      id: 'noc',
      label: 'NOC Dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      badge: 'BETA'
    }] : []),
    // RAG Testing - only for projects with FAQ Assist enabled
    ...(projectConfig.features.faqAssist ? [{
      id: 'rag-testing',
      label: 'RAG Testing',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      badge: 'DEV'
    }] : []),
    {
      id: 'analytics',
      label: 'Executive Analytics',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    // PDT Upload - only for PDT project
    ...(projectConfig.slug === 'pdt-project' ? [{
      id: 'pdt-upload',
      label: 'PDT Upload',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      badge: 'LIVE'
    }] : []),
    {
      id: 'architecture',
      label: 'System Architecture',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    }
  ];

  // Check if we should show form sections
  const showFormSections = settings.showPOCLinks &&
                           projectConfig.sidebarSections?.enabled &&
                           formSections.length > 0;

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-screen overflow-y-auto">
      <div className="p-4">
        {/* Form Sections */}
        {showFormSections && (
          <>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {projectConfig.sidebarSections?.title || 'Form Sections'}
            </h2>

            <div className="space-y-1 mb-8">
              {formSections.map((section) => (
                <Link
                  key={section.id}
                  to={`/${section.id}`}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                    activeSection === section.id
                      ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <span className="mr-3 text-lg">{typeof section.icon === 'string' ? section.icon : section.icon}</span>
                  {section.label}
                  {section.badge && (
                    <span className="ml-auto text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      {section.badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </>
        )}

        {/* Utility Items */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wide">Tools</h3>
          <div className="space-y-1">
            {utilityItems.map((item) => (
              <Link
                key={item.id}
                to={`/${item.id}`}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  activeSection === item.id
                    ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                {item.label}
                {(item as any).badge && (
                  <span className="ml-auto text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                    {(item as any).badge}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm text-gray-500">1 of 4</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full" style={{ width: '25%' }}></div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Complete all sections for final review</p>
        </div>
      </div>
    </div>
  );
};

export default SidebarNavigation;